import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Loader2, FileText, FileSpreadsheet, CheckSquare, Square,
         ChevronDown, ArrowLeftRight, Calendar, BarChart2, Users,
         ShoppingCart, Package, Award, Euro, RefreshCw, Target,
         Warehouse, Factory } from 'lucide-react';
import { toast } from 'sonner';
import { generatePDF, generateExcel } from '../utils/reportGenerators';

// ── Section definitions ────────────────────────────────────────────────────────
const ALL_SECTIONS = [
  {
    key: 'kpi',
    label: 'Riepilogo KPI',
    desc: 'Ordini totali, fatturato, imponibile, valore medio ordine',
    icon: BarChart2,
    color: 'text-green-600',
    group: 'annual',
  },
  {
    key: 'monthly',
    label: 'Andamento Mensile',
    desc: 'Trend mese per mese con fatturato e imponibile',
    icon: Calendar,
    color: 'text-blue-600',
    group: 'annual',
  },
  {
    key: 'status',
    label: 'Ordini per Stato',
    desc: 'Distribuzione ordini: confermato, spedito, consegnato...',
    icon: CheckSquare,
    color: 'text-purple-600',
    group: 'annual',
  },
  {
    key: 'clients',
    label: 'Top Clienti',
    desc: 'Classifica clienti per fatturato generato',
    icon: Users,
    color: 'text-amber-600',
    group: 'annual',
  },
  {
    key: 'products',
    label: 'Top Prodotti / Servizi',
    desc: 'Classifica prodotti per quantità venduta e fatturato',
    icon: Package,
    color: 'text-orange-600',
    group: 'annual',
  },
  {
    key: 'sales',
    label: 'Performance Commerciali',
    desc: 'Ordini e fatturato per ogni commerciale',
    icon: Award,
    color: 'text-rose-600',
    group: 'annual',
  },
  {
    key: 'invoices',
    label: 'Riepilogo Fatture',
    desc: 'Totale fatturato, incassato, da incassare e scaduto',
    icon: Euro,
    color: 'text-teal-600',
    group: 'annual',
  },
  {
    key: 'ltv',
    label: 'Valore Lifetime Clienti (LTV)',
    desc: 'Storico cumulativo: totale speso, frequenza, primo/ultimo ordine',
    icon: ShoppingCart,
    color: 'text-indigo-600',
    group: 'cumulative',
  },
  {
    key: 'forecast',
    label: 'Forecast & Budget',
    desc: 'Budget pianificato vs consuntivo reale, varianze mensili e per azienda/prodotto',
    icon: Target,
    color: 'text-emerald-600',
    group: 'forecast',
  },
  {
    key: 'inventory',
    label: 'Scorte Magazzino',
    desc: 'Stato scorte, materiali critici, valore magazzino e piano acquisti consigliato',
    icon: Warehouse,
    color: 'text-amber-600',
    group: 'inventory',
  },
  {
    key: 'production',
    label: 'Produzione & MRP',
    desc: 'Piano produzione vs consuntivo, fabbisogni materiali e copertura scorte',
    icon: Factory,
    color: 'text-blue-600',
    group: 'production',
  },
];

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_RANGE = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR - i);

function SectionCard({ sec, checked, onToggle }) {
  const Icon = sec.icon;
  return (
    <div
      onClick={() => onToggle(sec.key)}
      data-testid={`section-${sec.key}`}
      className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-all select-none ${
        checked
          ? 'border-primary bg-primary/5 ring-1 ring-primary/30'
          : 'border-border hover:bg-muted/50'
      }`}
    >
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
        checked ? 'bg-primary/10' : 'bg-muted'
      }`}>
        <Icon size={17} className={checked ? 'text-primary' : sec.color} />
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-semibold ${checked ? 'text-primary' : 'text-foreground'}`}>{sec.label}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{sec.desc}</p>
        {sec.group === 'cumulative' && (
          <span className="inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 font-medium">
            Storico cumulativo
          </span>
        )}
        {sec.group === 'forecast' && (
          <span className="inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 font-medium">
            Forecast & Budget
          </span>
        )}
        {sec.group === 'inventory' && (
          <span className="inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 font-medium">
            Scorte
          </span>
        )}
        {sec.group === 'production' && (
          <span className="inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-medium">
            Produzione
          </span>
        )}
      </div>
      <div className={`flex-shrink-0 mt-0.5 ${checked ? 'text-primary' : 'text-muted-foreground'}`}>
        {checked ? <CheckSquare size={18} /> : <Square size={18} />}
      </div>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────
export default function Reports() {
  const [selectedSections, setSelectedSections] = useState(['kpi', 'monthly', 'clients', 'products', 'sales']);
  const [mode, setMode]                          = useState('annual');     // 'annual' | 'comparison'
  const [year, setYear]                          = useState(CURRENT_YEAR);
  const [loading, setLoading]                    = useState(false);
  const [fetchState, setFetchState]              = useState('idle');       // 'idle' | 'fetching' | 'ready' | 'error'
  const [availableYears, setAvailableYears]      = useState(YEAR_RANGE);

  // Cached data
  const [annualData, setAnnualData]         = useState(null);
  const [prevAnnualData, setPrevAnnualData] = useState(null);
  const [cumulativeData, setCumulativeData] = useState(null);
  const [invoicesData, setInvoicesData]     = useState(null);
  const [forecastData, setForecastData]     = useState(null);
  const [inventoryData, setInventoryData]   = useState(null);
  const [productionData, setProductionData] = useState(null);

  // Fetch needed data
  const fetchData = useCallback(async () => {
    setFetchState('fetching');
    try {
      const needsCumulative  = selectedSections.includes('ltv');
      const needsInvoices    = selectedSections.includes('invoices');
      const needsForecast    = selectedSections.includes('forecast');
      const needsInventory   = selectedSections.includes('inventory');
      const needsProduction  = selectedSections.includes('production');

      const [annRes, prevRes, cumRes, invRes, varRes, sumRes, invDashRes, invMatRes, prodConsRes, prodReqRes] = await Promise.all([
        axios.get(`${API}/analytics/annual`, { params: { year } }),
        axios.get(`${API}/analytics/annual`, { params: { year: year - 1 } }),
        needsCumulative ? axios.get(`${API}/analytics/cumulative`) : Promise.resolve(null),
        needsInvoices   ? axios.get(`${API}/invoices/stats`)       : Promise.resolve(null),
        needsForecast   ? axios.get(`${API}/forecasts/variance`,  { params: { year, period_type: 'monthly' } }) : Promise.resolve(null),
        needsForecast   ? axios.get(`${API}/forecasts/summary`,   { params: { year } })                          : Promise.resolve(null),
        needsInventory  ? axios.get(`${API}/inventory/dashboard`)  : Promise.resolve(null),
        needsInventory  ? axios.get(`${API}/inventory/materials`)  : Promise.resolve(null),
        needsProduction ? axios.get(`${API}/production/consuntivo`, { params: { year } }) : Promise.resolve(null),
        needsProduction ? axios.get(`${API}/production/requirements`, { params: { year } }) : Promise.resolve(null),
      ]);

      setAnnualData(annRes.data);
      setPrevAnnualData(prevRes.data);
      if (cumRes)  setCumulativeData(cumRes.data);
      if (invRes)  setInvoicesData(invRes.data);
      if (varRes && sumRes) setForecastData({ variance: varRes.data, summary: sumRes.data });
      if (invDashRes && invMatRes) setInventoryData({ dashboard: invDashRes.data, materials: invMatRes.data });
      if (prodConsRes && prodReqRes) setProductionData({ consuntivo: prodConsRes.data, requirements: prodReqRes.data });

      // Sync available years
      const yrs = annRes.data?.available_years;
      if (yrs?.length) {
        const minY = Math.min(...yrs, CURRENT_YEAR - 3);
        setAvailableYears(Array.from({ length: CURRENT_YEAR - minY + 1 }, (_, i) => CURRENT_YEAR - i));
      }

      setFetchState('ready');
    } catch {
      toast.error('Errore nel caricamento dei dati');
      setFetchState('error');
    }
  }, [year, selectedSections]);

  useEffect(() => {
    setFetchState('idle');
  }, [year, mode, selectedSections]);

  const toggleSection = (key) => {
    setSelectedSections(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const selectAll   = () => setSelectedSections(ALL_SECTIONS.map(s => s.key));
  const deselectAll = () => setSelectedSections([]);

  const handleDownload = async (format) => {
    if (!selectedSections.length) { toast.error('Seleziona almeno una sezione'); return; }
    if (fetchState !== 'ready') {
      toast.error('Carica i dati prima di scaricare il report');
      return;
    }

    setLoading(true);
    try {
      const args = {
        sections: selectedSections,
        annualData,
        prevAnnualData,
        cumulativeData,
        invoicesData,
        forecastData,
        inventoryData,
        productionData,
        mode,
        year,
      };
      if (format === 'pdf') {
        generatePDF(args);
        toast.success('Report PDF generato con successo!');
      } else {
        generateExcel(args);
        toast.success('Report Excel generato con successo!');
      }
    } catch (err) {
      console.error(err);
      toast.error('Errore nella generazione del report');
    } finally {
      setLoading(false);
    }
  };

  const annualSections    = ALL_SECTIONS.filter(s => s.group === 'annual');
  const cumulSections     = ALL_SECTIONS.filter(s => s.group === 'cumulative');
  const forecastSections  = ALL_SECTIONS.filter(s => s.group === 'forecast');
  const inventorySections = ALL_SECTIONS.filter(s => s.group === 'inventory');
  const productionSections= ALL_SECTIONS.filter(s => s.group === 'production');
  const selectedCount     = selectedSections.length;
  const isReady           = fetchState === 'ready';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Generatore Report</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Scegli le sezioni da includere, la modalità di vista e scarica in PDF o Excel
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* ── LEFT: Section selection (2/3) ─────────────────────────────────── */}
        <div className="lg:col-span-2 space-y-5">

          {/* Controls row */}
          <div className="flex items-center justify-between flex-wrap gap-2">
            <span className="text-sm font-medium text-muted-foreground">
              {selectedCount}/{ALL_SECTIONS.length} sezioni selezionate
            </span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={selectAll} data-testid="select-all-btn">
                Seleziona tutte
              </Button>
              <Button variant="outline" size="sm" onClick={deselectAll} data-testid="deselect-all-btn">
                Deseleziona tutte
              </Button>
            </div>
          </div>

          {/* Annual sections */}
          <div>
            <p className="text-xs font-semibold uppercase text-muted-foreground tracking-wider mb-3">
              Dati Annuali
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {annualSections.map(sec => (
                <SectionCard
                  key={sec.key}
                  sec={sec}
                  checked={selectedSections.includes(sec.key)}
                  onToggle={toggleSection}
                />
              ))}
            </div>
          </div>

          {/* Cumulative sections */}
          <div>
            <p className="text-xs font-semibold uppercase text-muted-foreground tracking-wider mb-3">
              Dati Storici Cumulativi
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {cumulSections.map(sec => (
                <SectionCard
                  key={sec.key}
                  sec={sec}
                  checked={selectedSections.includes(sec.key)}
                  onToggle={toggleSection}
                />
              ))}
            </div>
          </div>

          {/* Forecast sections */}
          <div>
            <p className="text-xs font-semibold uppercase text-muted-foreground tracking-wider mb-3">
              Forecast & Budget
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {forecastSections.map(sec => (
                <SectionCard
                  key={sec.key}
                  sec={sec}
                  checked={selectedSections.includes(sec.key)}
                  onToggle={toggleSection}
                />
              ))}
            </div>
          </div>

          {/* Inventory sections */}
          <div>
            <p className="text-xs font-semibold uppercase text-muted-foreground tracking-wider mb-3">
              Scorte &amp; Magazzino
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {inventorySections.map(sec => (
                <SectionCard
                  key={sec.key}
                  sec={sec}
                  checked={selectedSections.includes(sec.key)}
                  onToggle={toggleSection}
                />
              ))}
            </div>
          </div>

          {/* Production sections */}
          <div>
            <p className="text-xs font-semibold uppercase text-muted-foreground tracking-wider mb-3">
              Produzione &amp; MRP
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {productionSections.map(sec => (
                <SectionCard
                  key={sec.key}
                  sec={sec}
                  checked={selectedSections.includes(sec.key)}
                  onToggle={toggleSection}
                />
              ))}
            </div>
          </div>
        </div>

        {/* ── RIGHT: Config + Download (1/3) ───────────────────────────────── */}
        <div className="space-y-4 lg:sticky lg:top-4">
          <Card className="border-border">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Configurazione Report
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">

              {/* Year selector */}
              <div>
                <label className="text-xs font-medium text-muted-foreground block mb-2">Anno di riferimento</label>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setYear(y => y - 1)}
                    className="w-7 h-7 flex items-center justify-center rounded-lg border border-border hover:bg-muted transition-colors text-muted-foreground text-sm"
                    data-testid="report-year-prev"
                  >‹</button>
                  <div className="flex gap-1 flex-wrap">
                    {availableYears.slice(0, 5).map(y => (
                      <button
                        key={y}
                        onClick={() => setYear(y)}
                        data-testid={`report-year-${y}`}
                        className={`px-2.5 py-1 rounded-lg text-xs font-bold border transition-all ${
                          y === year
                            ? 'bg-primary text-primary-foreground border-primary'
                            : 'border-border hover:bg-muted'
                        }`}
                      >
                        {y}
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={() => setYear(y => y + 1)}
                    className="w-7 h-7 flex items-center justify-center rounded-lg border border-border hover:bg-muted transition-colors text-muted-foreground text-sm"
                    data-testid="report-year-next"
                  >›</button>
                </div>
              </div>

              {/* View mode */}
              <div>
                <label className="text-xs font-medium text-muted-foreground block mb-2">Modalità vista</label>
                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => setMode('annual')}
                    data-testid="mode-annual"
                    className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm font-medium transition-all text-left ${
                      mode === 'annual'
                        ? 'border-primary bg-primary/5 text-primary'
                        : 'border-border hover:bg-muted text-muted-foreground'
                    }`}
                  >
                    <Calendar size={14} className="flex-shrink-0" />
                    <div>
                      <p className="text-xs font-semibold">Vista Annuale</p>
                      <p className="text-[10px] text-muted-foreground">Solo anno {year}</p>
                    </div>
                  </button>
                  <button
                    onClick={() => setMode('comparison')}
                    data-testid="mode-comparison"
                    className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm font-medium transition-all text-left ${
                      mode === 'comparison'
                        ? 'border-primary bg-primary/5 text-primary'
                        : 'border-border hover:bg-muted text-muted-foreground'
                    }`}
                  >
                    <ArrowLeftRight size={14} className="flex-shrink-0" />
                    <div>
                      <p className="text-xs font-semibold">Vista Confronto</p>
                      <p className="text-[10px] text-muted-foreground">{year} colonna a colonna con {year - 1}</p>
                    </div>
                  </button>
                </div>
              </div>

              {/* Preview summary */}
              <div className="rounded-lg bg-muted/60 p-3 text-xs space-y-1">
                <p className="font-semibold text-foreground">Anteprima report:</p>
                <p className="text-muted-foreground">
                  {selectedCount === 0
                    ? 'Nessuna sezione selezionata'
                    : `${selectedCount} sezioni · Anno ${year}${mode === 'comparison' ? ` vs ${year - 1}` : ''}`}
                </p>
                {selectedSections.map(k => {
                  const s = ALL_SECTIONS.find(s => s.key === k);
                  return s ? <p key={k} className="text-muted-foreground pl-2">· {s.label}</p> : null;
                })}
              </div>

              {/* Fetch + Download buttons */}
              <div className="space-y-2 pt-1">
                <Button
                  onClick={fetchData}
                  variant="outline"
                  className="w-full gap-2"
                  disabled={fetchState === 'fetching' || selectedCount === 0}
                  data-testid="load-data-btn"
                >
                  {fetchState === 'fetching'
                    ? <><Loader2 size={15} className="animate-spin" />Caricamento dati...</>
                    : <><RefreshCw size={15} className={isReady ? 'text-green-600' : ''} />
                      {isReady ? '✓ Dati pronti — Ricarica' : 'Carica dati'}</>
                  }
                </Button>

                <Button
                  onClick={() => handleDownload('pdf')}
                  className="w-full gap-2 bg-rose-600 hover:bg-rose-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={loading || !isReady || selectedCount === 0}
                  data-testid="download-pdf-btn"
                >
                  {loading ? <Loader2 size={15} className="animate-spin" /> : <FileText size={15} />}
                  Scarica PDF
                </Button>

                <Button
                  onClick={() => handleDownload('excel')}
                  className="w-full gap-2 bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={loading || !isReady || selectedCount === 0}
                  data-testid="download-excel-btn"
                >
                  {loading ? <Loader2 size={15} className="animate-spin" /> : <FileSpreadsheet size={15} />}
                  Scarica Excel
                </Button>
              </div>

              {fetchState === 'idle' && selectedCount > 0 && (
                <p className="text-xs text-amber-600 dark:text-amber-400 text-center">
                  Clicca "Carica dati" prima di scaricare
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
